import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../components/toast/useToast';
import MeteoService from './MeteoService';
import {
    HydroStanice,
    MeteoConfigDto,
    MeteoConfigResponse,
    MeteoHydroConfigDto,
    MeteoStatusDto,
    OrpEntry,
} from '../generated/models/Meteo';

/** ORP číselník — 206 záznamů cached 24h server-side, FE cache infinity. */
export const useOrpCiselnikQuery = () => {
    return useQuery<OrpEntry[], Error>({
        queryKey: ['meteoOrpCiselnik'],
        queryFn: () => MeteoService.getOrpCiselnik(),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });
};

export const useMeteoConfigQuery = () => {
    return useQuery<MeteoConfigResponse, Error>({
        queryKey: ['meteoConfig'],
        queryFn: () => MeteoService.getConfig(),
        staleTime: 60 * 1000,
    });
};

/** Status pro FE health banner — 30s refetch (NEVOLÁ chmu live, čte jen jsdh-be lokální state). */
export const useMeteoStatusQuery = () => {
    return useQuery<MeteoStatusDto, Error>({
        queryKey: ['meteoStatus'],
        queryFn: () => MeteoService.getStatus(),
        staleTime: 30 * 1000,
        refetchInterval: 30 * 1000,
    });
};

export const useUpdateMeteoConfigMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<MeteoConfigResponse, Error, MeteoConfigDto>({
        mutationFn: (dto) => MeteoService.updateConfig(dto),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['meteoConfig'] });
            queryClient.invalidateQueries({ queryKey: ['meteoStatus'] });
            // Side effect: PUT může zavřít AUTO pohotovosti při disable/odebrání cisorp
            queryClient.invalidateQueries({ queryKey: ['pohotovostVsechny'] });
            queryClient.invalidateQueries({ queryKey: ['pohotovostAktivni'] });
            showToast('Meteo konfigurace uložena.', 'success');
        },
        onError: (error) => {
            showToast(error.message || 'Chyba při ukládání meteo konfigurace.', 'error');
        },
    });
};

// HO-94 E4 hydro hooks

/** Hydro stanice číselník (~423 použitelných z chmu-service). Cache 24h. */
export const useHydroCiselnikQuery = () => {
    return useQuery<HydroStanice[], Error>({
        queryKey: ['meteoHydroCiselnik'],
        queryFn: () => MeteoService.getHydroCiselnik(),
        staleTime: 24 * 60 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

export const useHydroConfigQuery = () => {
    return useQuery<MeteoHydroConfigDto, Error>({
        queryKey: ['meteoHydroConfig'],
        queryFn: () => MeteoService.getHydroConfig(),
        staleTime: 60 * 1000,
    });
};

export const useUpdateHydroConfigMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<MeteoHydroConfigDto, Error, MeteoHydroConfigDto>({
        mutationFn: (dto) => MeteoService.updateHydroConfig(dto),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['meteoHydroConfig'] });
            queryClient.invalidateQueries({ queryKey: ['meteoStatus'] });
            queryClient.invalidateQueries({ queryKey: ['pohotovostVsechny'] });
            queryClient.invalidateQueries({ queryKey: ['pohotovostAktivni'] });
            showToast('Hydro konfigurace uložena.', 'success');
        },
        onError: (error) => {
            showToast(error.message || 'Chyba při ukládání hydro konfigurace.', 'error');
        },
    });
};
